const moneyValue = value => {
  if (typeof value !== 'string' || !/^\$[\d,]+\.\d{2}$/.test(value)) return NaN;
  return Number(value.replace(/[$,]/g, ''));
};

export function monthlyPayment(price, apr, months) {
  const rate = apr / 1200;
  return rate === 0 ? price / months : price * rate / (1 - Math.pow(1 + rate, -months));
}

// Split-pay amounts are never relabeled as monthly payments.
export function monthlyEstimate(meta, variantId, priceCents) {
  if (!Number.isFinite(priceCents) || priceCents < 15000 || priceCents > 3000000) return null;
  const price = priceCents / 100;
  if (!meta) return { amount: monthlyPayment(price, 15, 12), apr: 15, months: 12 };
  if (meta.currency_code !== 'USD' || meta.country_code !== 'US') return null;
  const variant = meta.variants?.find(item => String(item.id) === String(variantId));
  if (!variant?.eligible || variant.available === false) return null;
  const quotes = (meta.financing_plans || []).flatMap(plan => {
    const min = moneyValue(plan.min_price);
    const max = moneyValue(plan.max_price);
    if (!Number.isFinite(min) || !Number.isFinite(max) || price < min || price > max) return [];
    return (plan.terms || []).filter(term =>
      term.loan_type === 'interest' && Number.isFinite(term.apr) && term.apr >= 0 && term.apr <= 100 &&
      Number.isInteger(term.installments_count) && term.installments_count > 0 && term.installments_count <= 60
    ).map(term => ({
      amount: monthlyPayment(price, term.apr, term.installments_count),
      apr: term.apr,
      months: term.installments_count,
    }));
  });
  return quotes.sort((a, b) => a.amount - b.amount)[0] || null;
}

if (typeof customElements !== 'undefined' && !customElements.get('smarttract-installments')) {
  customElements.define('smarttract-installments', class extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      this.terms = this.querySelector('shopify-payment-terms');
      this.productInfo = this.closest('product-info');
      this.variants = JSON.parse(this.querySelector('[data-emi-variants]').textContent);
      this.onChange = () => queueMicrotask(() => this.refresh());
      this.form?.addEventListener('change', this.onChange);
      this.observer = new MutationObserver(this.onChange);
      if (this.terms) this.observer.observe(this.terms, { attributes: true, attributeFilter: ['variant-id', 'shopify-meta'] });
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, event => {
          if (String(event.data.sectionId) === this.dataset.section) this.refresh();
        });
      }
      this.onPlanClick = event => {
        const summary = event.target.closest('summary');
        if (!summary || !this.contains(summary)) return;
        const details = summary.parentElement;
        if (details.open) return;
        // Keep Shopify's form and its native control intact. If its control is not
        // ready, the disclosure opens normally so the customer can use Learn more.
        const nativeButton = this.terms?.shadowRoot?.querySelector('#shopify-installments button');
        if (nativeButton && !nativeButton.disabled) {
          event.preventDefault();
          details.open = true;
          nativeButton.click();
        }
      };
      this.addEventListener('click', this.onPlanClick);
      this.refresh();
    }

    disconnectedCallback() {
      this.form?.removeEventListener('change', this.onChange);
      this.observer?.disconnect();
      this.unsubscribe?.();
      this.removeEventListener('click', this.onPlanClick);
    }

    refresh() {
      const id = this.form?.querySelector('[name="id"]')?.value;
      const variant = this.variants.find(item => String(item.id) === String(id));
      let meta = null;
      try { meta = JSON.parse(this.terms?.getAttribute('shopify-meta') || 'null'); } catch { /* Use the disclosed server example. */ }
      const quote = monthlyEstimate(meta, id, variant?.price);
      const sectionId = this.dataset.section;
      const sticky = document.getElementById(`stickyAtcBar-${sectionId}`) || document.querySelector(`.sticky-atc-bar[data-section-id="${sectionId}"]`);
      const roots = [this.productInfo || this, sticky].filter(Boolean);
      const format = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });
      for (const root of roots) {
        // The theme replaces the price row after variant selection; query fresh nodes.
        root.querySelectorAll('[data-emi-estimate]').forEach(node => { node.hidden = !quote; });
        if (quote) {
          root.querySelectorAll('[data-emi-amount]').forEach(node => { node.textContent = format.format(quote.amount); });
          const note = `*Example: ${quote.months} monthly payments at ${quote.apr}% APR. Subject to eligibility and approval.`;
          root.querySelectorAll('[data-emi-note]').forEach(node => { node.textContent = note; });
          root.querySelectorAll('[data-emi-estimate]').forEach(node => { node.title = note.slice(1); });
        }
      }
      const stickyPrice = sticky?.querySelector('[data-sticky-current-price]');
      if (stickyPrice && variant) stickyPrice.textContent = format.format(variant.price / 100);
    }
  });
}
